// Redux
import { AppThunk } from '@redux/configureStore';
import { createSlice } from '@reduxjs/toolkit';
// Api
import { apiGetHistoricalReport } from '@api/payslips';
import { apiGetPeriodsByYear } from '@api/utils';
import { apiGetPilaCertification, apiGetPilaSpreadSheet } from '@api/pilaReport';
// Models
import { IPeriods } from '@models/Periods';
import { IReportsState } from '@models/Reports';
import { IPilaSpreadSheet } from '@models/PilaSpreadSheet';

const initialState: IReportsState = {
    periods: [],
    error: '',
    hasYear: false,
    currentYear: '',
    pilaReport: [],
    data: [],
    pilaCertificate: [],
    openModal: false,
    payrollReport: {
        period: '',
        activeEmployees: [],
        inactiveEmployees: [],
        employeesContractInfo: {
            new_incomes: 0,
            pay_off_employees: 0,
        },
    },
};

const reportsSlice = createSlice({
    name: 'reports',
    initialState,
    reducers: {
        setPeriods: (state, action) => {
            state.periods = action.payload;
        },
        setHasYear: (state, action) => {
            state.hasYear = action.payload;
        },
        setPayrollReport: (state, action) => {
            state.payrollReport = action.payload;
        },
        setCurrentYear: (state, action) => {
            state.currentYear = action.payload;
        },
        setResetAllData: state => {
            state.periods = [];
            state.payrollReport = {
                period: '',
                activeEmployees: [],
                inactiveEmployees: [],
                employeesContractInfo: {
                    new_incomes: 0,
                    pay_off_employees: 0,
                },
            };
            state.pilaReport = [];
            state.pilaCertificate = [];
            state.hasYear = false;
            state.currentYear = '';
        },
        setPilaReport: (state, action) => {
            state.pilaReport = action.payload;
        },
        setPilaCertificate: (state, action) => {
            state.pilaCertificate = action.payload;
        },
        setData: (state, action) => {
            state.data = action.payload;
        },
        setModal: (state, action) => {
            state.openModal = action.payload;
        },
        failedRequest: (state, action) => {
            state.error = action.payload;
        },
    },
});

export const getHistoricalPayrollReports = (periodId: string): AppThunk => {
    return async (dispatch): Promise<void> => {
        try {
            const response = await apiGetHistoricalReport(periodId);
            dispatch(setPayrollReport(response));
        } catch (error) {
            dispatch(failedRequest(error));
        }
    };
};

export const getPeriodsByYear = (year: string): AppThunk => {
    return async (dispatch): Promise<void> => {
        try {
            const response = await apiGetPeriodsByYear<IPeriods[]>(year);
            if (!Array.isArray(response)) {
                dispatch(setPeriods([]));
                return;
            }
            dispatch(setPeriods(response));
        } catch (error) {
            dispatch(failedRequest(error));
        }
    };
};

export const getPilaSpreadSheet = (body: { year_period_id: string }): AppThunk => {
    const MAIN_INDEX = 0;
    return async (dispatch): Promise<void> => {
        try {
            const response = await apiGetPilaSpreadSheet<IPilaSpreadSheet[]>(body);
            if (!Array.isArray(response) || response.length === 0) {
                dispatch(setPilaReport([]));
                return;
            }
            if (response[MAIN_INDEX].Liquidaciones.length === 0) {
                dispatch(setPilaReport([]));
                return;
            }
            dispatch(setPilaReport(response[MAIN_INDEX].Liquidaciones[MAIN_INDEX].DetallesLiquidacionEmpleados));
        } catch (error) {
            dispatch(failedRequest(error));
        }
    };
};

export const getPilaCertification = (body: { start_date: string; end_date: string }): AppThunk => {
    return async (dispatch): Promise<void> => {
        try {
            const response = await apiGetPilaCertification(body);
            if (!Array.isArray(response)) {
                dispatch(setPilaReport([]));
                return;
            }
            const employeesHasCertification = response.filter(item => item.contribution_certificate);
            if (!employeesHasCertification.length) {
                dispatch(setPilaCertificate([]));
                dispatch(setModal(true));
                return;
            }
            dispatch(setPilaCertificate(employeesHasCertification));
        } catch (error) {
            dispatch(failedRequest(error));
        }
    };
};

export default reportsSlice.reducer;

export const {
    setPeriods,
    setHasYear,
    setCurrentYear,
    setResetAllData,
    setPayrollReport,
    setPilaReport,
    setPilaCertificate,
    setData,
    setModal,
    failedRequest,
} = reportsSlice.actions;
